/* eslint-disable no-case-declarations */
/* eslint-disable indent */
/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { RequestState } from '@shared/models/request-state';
import { AlertService } from '@shared/services/alert/alert.service';
import { CrewService } from './crew.service';
import { CrewRequest } from './models/request/crew.request';
import { CrewEntryResponse } from './models/response/crew-entry.response';
import { CrewListResponse } from './models/response/crew-list.response';
import { CrewListView } from './models/view/crew-list.view';
import { DateTime } from 'luxon';
import { CrewLocationView } from './models/view/crew-location.view';
import { AuthService } from '@shared/services/auth.service';
import { CrewHistoryService } from './crew-history.service';

@Injectable({
  providedIn: 'root',
})
export class CrewStore {
  private readonly showSeqNumbersKey = 'crew.showSequenceNumbers';

  private _showSeqNumbers = new BehaviorSubject<boolean>(false);
  showSeqNumbers$ = this._showSeqNumbers.asObservable();

  constructor(
    private crewService: CrewService,
    private authService: AuthService,
    private crewHistoryService: CrewHistoryService,
    private alertService: AlertService,
  ) {
    // Set show sequence numbers setting from local storage.
    if (localStorage.getItem(this.showSeqNumbersKey)) {
      const value = JSON.parse(
        localStorage.getItem(this.showSeqNumbersKey) || 'false',
      );
      this._showSeqNumbers.next(value);
    }
  }

  setShowSeqNumbers(newValue: boolean): void {
    localStorage.setItem(this.showSeqNumbersKey, JSON.stringify(newValue));
    this._showSeqNumbers.next(newValue);
  }

  /** Uses the cache to return the requested crew list. */
  getCrew(
    crewRequest: CrewRequest,
    isRefresh: boolean,
  ): Observable<RequestState<CrewListView>> {
    return this.crewService.getCrewList(crewRequest, isRefresh).pipe(
      map((requestState) => {
        if (requestState.hasError) {
          this.showErrorAlert(crewRequest.flightNumber);
        }

        return this.mapResponse(requestState);
      }),
    );
  }

  getCrewLocation(
    crewRequest: CrewRequest,
    isRefresh: boolean,
  ): Observable<RequestState<CrewLocationView>> {
    return this.crewService.getCrewLocation(crewRequest, isRefresh).pipe(
      map((requestState) => {
        if (requestState.hasError) {
          this.showErrorAlert(crewRequest.flightNumber);
        }

        if (!requestState.response) {
          return requestState.into<CrewLocationView>();
        }

        return requestState.into<CrewLocationView>(
          this.parseCrewLocation(requestState.response?.result || ''),
        );
      }),
    );
  }

  /** Creates appropriate response depending on whether a response exists. */
  private mapResponse(
    requestState: RequestState<CrewListResponse>,
  ): RequestState<CrewListView> {
    const crewListView = this.mapCrewListView(requestState.response || null);

    if (crewListView) {
      return requestState.into<CrewListView>(crewListView);
    }

    return requestState.into<CrewListView>();
  }

  // eslint-disable-next-line complexity
  /** Create CrewListView from API response. */
  private mapCrewListView(
    response: CrewListResponse | null,
  ): CrewListView | null {
    if (!response) {
      return null;
    }

    // Remove dashes from gate numbers if they exist.
    response.gate.depart = response.gate.depart?.replace(/-/g, '');
    response.gate.arrive = response.gate.arrive?.replace(/-/g, '');

    // Divide pilot and F/A entries into separate arrays.
    const pilots: CrewEntryResponse[] = [];
    const flightAttendants: CrewEntryResponse[] = [];

    // Modify names and separate pilots and flight attendants.
    for (let crewMember of response.flight) {
      // Remove first/middle initial from name if a first or nickname exists.
      // Also capitalize first letter of names except cases like 'III'
      if (crewMember.firstName || crewMember.nickname) {
        crewMember = this.removeSpecialChars(crewMember);

        const lastName = this.getLastName(crewMember.name);
        // First name is already title-cased.
        crewMember.name = this.titleCase(lastName);
      }

      // If name is 'OPEN' remove base to make entry mostly blank.
      // Also set fitForDuty to null so it doesn't get displayed.
      if (crewMember.name === 'OPEN') {
        crewMember.base = '';
        crewMember.fitForDuty = null;
      }

      // Remove everything except numbers from phone numbers.
      const phoneReplacePattern = /[()-\s]/g;
      if (crewMember.contact?.homePhone) {
        crewMember.contact.homePhone = crewMember.contact.homePhone.replace(
          phoneReplacePattern,
          '',
        );
      }
      if (crewMember.contact?.mobilePhone) {
        crewMember.contact.mobilePhone = crewMember.contact.mobilePhone.replace(
          phoneReplacePattern,
          '',
        );
      }

      // Adjust sequence date. The month may not be accurate.
      if (response.request && crewMember.sequenceDate) {
        const flightDate = DateTime.fromISO(response.request.date);

        // Start with flight date, substitute sequence date day, then adjust month if needed.
        let seqDate = DateTime.fromObject({
          month: flightDate.month,
          day: parseInt(crewMember.sequenceDate.substring(8)),
          year: flightDate.year,
        });

        if (!seqDate.isValid) {
          // Decrement month if date is invalid.
          let month = flightDate.month;
          let year = flightDate.year;

          if (month === 1) {
            month = 12;
            year -= 1;
          } else {
            month -= 1;
          }

          seqDate = DateTime.fromObject({
            month,
            day: parseInt(crewMember.sequenceDate.substring(8)),
            year,
          });
        } else if (flightDate.day < seqDate.day) {
          // If the flight day is greater than the sequence day, the seq date month must be one prior.
          seqDate = seqDate.minus({ months: 1 });
        }

        crewMember.sequenceDate = seqDate.toISODate();
      }

      // Identify pilots vs flight attendants.
      if (
        crewMember.seat === 'CA' ||
        crewMember.seat === 'FO' ||
        crewMember.seat === 'FB' ||
        crewMember.seat === 'FC' ||
        crewMember.seat === 'RC' ||
        crewMember.seat === 'XA'
      ) {
        if (
          crewMember.employeeNumber !== this.authService.empNumber &&
          crewMember.employeeNumber !== ''
        ) {
          // Create history observable for all pilots except self.
          crewMember.history$ = this.crewHistoryService.getFlightsFlownWith(
            crewMember.employeeNumber,
          );
        }
        pilots.push(crewMember);
      } else {
        // Set fit for duty to null.
        crewMember.fitForDuty = null;
        flightAttendants.push(crewMember);
      }
    }

    // Modify certain deadhead items.
    for (let dh of response.deadhead) {
      // Set fit for duty to null.
      dh.fitForDuty = null;

      // Remove deadheader initials if a first or nickname exists.
      if (dh.firstName || dh.nickname) {
        dh = this.removeSpecialChars(dh);
        const lastName = this.getLastName(dh.name);
        dh.name = this.titleCase(lastName);
      }

      // Remove - or * from seat.
      dh.seat = dh.seat.replace(/[*-]/, '');

      // Remove sequence number if not mainline employee
      if (dh.employeeNumber.length > 6 && dh.sequenceNumber) {
        dh.sequenceNumber = undefined;
      }

      // Create history observable for mainline pilot deadheaders.
      if (
        (dh.seat === 'CA' ||
          dh.seat === 'FO' ||
          dh.seat === 'FB' ||
          dh.seat === 'FC' ||
          dh.seat === 'RC') &&
        dh.employeeNumber.length === 6 &&
        dh.employeeNumber !== this.authService.empNumber &&
        dh.employeeNumber !== ''
      ) {
        // Create history observable for all pilots except self.
        dh.history$ = this.crewHistoryService.getFlightsFlownWith(
          dh.employeeNumber,
        );
      }
    }

    // Modify certain deadhead items (training DHs).
    for (let dh of response.deadheadTraining) {
      // Set fit for duty to null.
      dh.fitForDuty = null;

      // Remove deadheader initials if a first or nickname exists.
      if (dh.firstName || dh.nickname) {
        dh = this.removeSpecialChars(dh);
        const lastName = this.getLastName(dh.name);
        dh.name = this.titleCase(lastName);
      }

      // Remove - or * from seat.
      dh.seat = dh.seat.replace(/[*-]/, '');
    }

    const crewListView: CrewListView = {
      timestamp: DateTime.fromISO(response.timestamp, { zone: 'utc' }),
      pilots,
      flightAttendants,
      deadhead: response.deadhead.filter((dh) => dh.name !== ''),
      deadheadTraining: response.deadheadTraining,
      gate: response.gate,
    };

    return crewListView;
  }

  /** Sometimes a first or nickname is *, -, etc. Make these blank. */
  private removeSpecialChars(crewMember: CrewEntryResponse): CrewEntryResponse {
    if (
      crewMember.firstName === '*' ||
      crewMember.firstName === '--' ||
      crewMember.firstName === '.' ||
      crewMember.firstName === '$'
    ) {
      crewMember.firstName = '';
    }

    if (
      crewMember.nickname === '*' ||
      crewMember.nickname === '--' ||
      crewMember.nickname === '.' ||
      crewMember.nickname === '$'
    ) {
      crewMember.nickname = '';
    }

    return crewMember;
  }

  /** Extract last name from name string. Ex: TREVARTON from TREVARTON VD */
  private getLastName(name: string): string {
    const words = name.split(' ');
    let lastName = '';

    if (words.length > 1) {
      // Remove the last group of chars, which should be the first/middle initial.
      words.pop();
      lastName = words.join(' ');
    } else if (words.length === 1) {
      // If no first initial.
      lastName = words[0];
    }

    return lastName;
  }

  // Set first letter of each name to uppercase except trailing chars like 'III'.
  // The trailing chars are the reason for not using the titlecase pipe.
  private titleCase(name: string): string {
    return name
      .split(' ')
      .map((namePart, index) => {
        const trimmed = namePart.trim();

        if (index === 0 && trimmed.length > 0) {
          // Title case first part of last name.
          const firstLetter = trimmed.charAt(0).toUpperCase();
          return `${firstLetter}${trimmed.substring(1).toLowerCase()}`;
        } else if (trimmed.length < 4) {
          // Assume a short second part is an abbreviation like JR or III, don't titlecase.
          return trimmed.toUpperCase();
        } else {
          const firstLetter = trimmed.charAt(0).toUpperCase();
          return `${firstLetter}${trimmed.substring(1).toLowerCase()}`;
        }
      })
      .join(' ');
  }

  /** Extract pertinent data from NSG result. */
  private parseCrewLocation(text: string): CrewLocationView {
    /* Example:
              1         2         3         4         5         6
    0123456789012345678901234567890123456789012345678901234567890123

    1832/08 321R N887  FROM 2567/08 IN 1330/08 AS OF 19SEP23/1554
    STA SKD      LATEST       GATE      S/R
    DEP DFW 1450/08  OUT 1502/08  A15
    ARV DEN 1555/08  IN  1559/08  C34                  TCAS OPN
    BSE ST NAME              EMP   SEQ     ARRIVAL         GTE L
    DFWFFO SHUSTER       CA353224 16937/07  841   IN  1300 B9
    DFWFCA LYNCH         FO653467 16937/07  841   IN  1300 B9  R
    DFW 01 WILLIAMS J      820631 15505/07  841   IN  1300 B9  R
    DFW 02 WARD J          687290 15505/07  841   IN  1300 B9
    DFW 03 MOUZON A        650869 15505/07  841   IN  1300 B9
    DFW 04 CUEVAS K        239578 15505/07  841   IN  1300 B9  R
    DEADHEADING
    BSE ST NAME              EMP   SEQ     ARRIVAL         GTE TKT
    DFW CA JORDAN MA       517980          UNKN                A12
    PHL CA SCHMITZ D       462530          UNKN                A12
    DFW FO CONRAD GL       663926          UNKN                A12
    END
    */

    const crewLocationView: CrewLocationView = {};
    const lines = text.split('\n');

    for (const line of lines) {
      const employeeNumber = line.substring(23, 29);

      // If line contains a 6 digit employee number in the correct position, parse it.
      if (/\d{6}/.test(employeeNumber)) {
        const arrivalFlight = line.substring(39, 44).trim();
        const arrivalStatus = line.substring(46, 49).trim();
        const arrivalTime = line.substring(50, 54).trim();
        let arrivalGate = line.substring(55, 59).trim();

        // Remove X that might be present in last column of gate (due to XR in next column).
        if (
          arrivalGate.length > 0 &&
          arrivalGate.charAt(arrivalGate.length - 1) === 'X'
        ) {
          arrivalGate = arrivalGate.slice(0, arrivalGate.length - 1);
        }

        let isFromHotel = false;
        let isFromSameAircraft = false;
        let isOrig = false;

        if (arrivalFlight === 'HTL') {
          isFromHotel = true;
        }

        if (arrivalFlight === 'C/E') {
          isFromSameAircraft = true;
        }

        if (arrivalFlight === 'ORIG') {
          isOrig = true;
        }

        crewLocationView[employeeNumber] = {
          arrivalFlight,
          arrivalStatus,
          arrivalTime,
          arrivalGate,
          isFromHotel,
          isFromSameAircraft,
          isOrig,
        };
      }
    }

    return crewLocationView;
  }

  /** Show alert and return null on error. */
  private showErrorAlert(flightNumber: string): void {
    this.alertService.setAlert({
      type: 'error',
      message: `Failed to load crew data for flight ${flightNumber}.`,
    });
  }
}
